import { Box, Drawer, Stack } from "@mui/material";
import { StyledBox } from "./ListingFilters.styles";
import { useEffect, useMemo, useState, Fragment } from "react";
import {
  BaseButton,
  Checkbox,
  FlagImg,
  Input,
  LgTextSemiBold,
  Row,
  SmText,
  TextButton,
  TextMd,
  YellowButton,
} from "Components";
import { useIntl } from "react-intl";
import useGetData from "hooks/useGetData";
import { apiGetListingFilters } from "services/api/listing";
import { ListingFilterType, ListingFilterValueType } from "types/listing";
import { FilterIcon } from "Assets/icons";
import { COLORS } from "constants/layout";
import { useLocation } from "react-router-dom";
import { getSearchParam } from "utils/searchParamsUtils";
import { ALink } from "../link";
import { APP_IMGS } from "Assets/images";
import useBreakpoints from "hooks/useBreakpoint";
import { PATHS } from "constants/routes";

type Props = {
  drawerMode?: boolean;
  categoryId?: string;
};

const hasSearchOptions = ["MULTI_SELECT", "LINK"];

const ListingFilters = (props: Props) => {
  const t = useIntl();
  const location = useLocation();
  const { drawerMode, categoryId } = props;
  const [showDrawer, setShowDrawer] = useState(false);
  const { isMobile } = useBreakpoints();
  const { data } = useGetData({
    promise: () => {
      const currentUrl = `${window.location.origin}${location.pathname}${location.search}`;
      const urlWithCategory = categoryId ? `${currentUrl}&category_id=${categoryId}` : currentUrl;
      return apiGetListingFilters(urlWithCategory);
    },
    deps: [location, categoryId],
  });

  const filters: ListingFilterType[] = useMemo(() => data?.data || [], [data]);

  const onCloseDrawer = () => setShowDrawer(false);

  const onClickFilterDrawer = () => setShowDrawer(true);

  const Component = ({ isTmp }: { isTmp?: boolean }) => {
    return (
      <Fragment>
        <StyledBox spacing={2} height={isTmp ? "100%" : "auto"}>
          <LgTextSemiBold className="title">
            {t.formatMessage({ id: "filters" })}
          </LgTextSemiBold>

          <Box flex={1}>
            {filters.map((filter, i) => (
              <Stack key={i} mb={2} spacing={1}>
                <TextMd>{filter.section}</TextMd>
                <DynamicFilter {...{ filter }} />
              </Stack>
            ))}
          </Box>

          {isTmp && (
            <Row className="tmp-box">
              <BaseButton className="clear-btn" onClick={onCloseDrawer}>
                {t.formatMessage({ id: "clear_all" })}
              </BaseButton>
              <YellowButton onClick={onCloseDrawer}>
                {t.formatMessage({ id: "done" })}
              </YellowButton>
            </Row>
          )}
        </StyledBox>
        <StyledBox
          style={{
            backgroundColor: "none",
            padding: "0px",
            marginTop: "8px",
            cursor: "pointer",
          }}
        >
          {!isMobile && (
            <img
              src={APP_IMGS.common.desktop["bannerFilterForBuyerLead"]}
              alt="banner"
              className="bannerFilter"
              loading="lazy"
              onClick={() =>
                window.open(
                  `${PATHS.supplierPricing}?tab=half_yearly`,
                  "_blank"
                )
              }
            />
          )}
        </StyledBox>
      </Fragment>
    );
  };

  if (drawerMode) {
    return (
      <Fragment>
        <TextButton
          startIcon={<FilterIcon />}
          sx={{ fontSize: 14 }}
          onClick={onClickFilterDrawer}
        >
          {t.formatMessage({ id: "filter" })}
        </TextButton>
        <Drawer
          open={showDrawer}
          onClose={onCloseDrawer}
          anchor="right"
          PaperProps={{ sx: { width: "60%" } }}
        >
          <Component isTmp />
        </Drawer>
      </Fragment>
    );
  }

  return <Component />;
};

const DynamicFilter = ({ filter }: { filter: ListingFilterType }) => {
  const { value, type, param } = filter;
  const location = useLocation();
  const t = useIntl();
  const searchParams = new URLSearchParams(location.search);
  const multipleParam = searchParams.get(param);

  const [search, setSearch] = useState("");
  const [multipleData] = useState<string[]>(
    multipleParam ? multipleParam.split(",") : []
  );
  const [formattedValue, setFormattedValue] =
    useState<ListingFilterValueType[]>(value);

  const onDirectHref = (href: string) => {
    window.location.href = href;
  };

  const updateSearchParams = (searchParams: URLSearchParams) => {
    searchParams.delete("page");
    const queryString = searchParams.toString();
    onDirectHref(
      queryString ? `${location.pathname}?${queryString}` : location.pathname
    );
  };

  const onChangeMultipleData = (code: string) => {
    const updatedMultipleData = multipleData.includes(code)
      ? multipleData.filter((c) => c !== code)
      : [...multipleData, code];
    const searchParams = new URLSearchParams(location.search);
    if (updatedMultipleData.length > 0) {
      searchParams.set(param, updatedMultipleData.join(","));
    } else {
      searchParams.delete(param);
    }
    updateSearchParams(searchParams);
  };

  const onChangeSelect = (newValue: string) => {
    const searchParams = new URLSearchParams(location.search);
    if (String(newValue) === searchParams.get(param)) {
      searchParams.delete(param);
    } else {
      searchParams.set(param, newValue);
    }
    updateSearchParams(searchParams);
  };

  const Component = useMemo(() => {
    switch (type) {
      case "LINK":
        return () => (
          <Fragment>
            {formattedValue.map((v, i) => (
              <ALink key={i} href={v.url}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {v.code && (
                    <FlagImg country_code={v.code} className="flag-img" />
                  )}
                  <SmText color={COLORS.gray[700]} className="ellipsis-text">
                    {v.label}
                  </SmText>
                </Stack>
              </ALink>
            ))}
          </Fragment>
        );

      case "MULTI_SELECT":
        return () => (
          <Fragment>
            {formattedValue
              .sort((v) => (multipleData.includes(v.value) ? -1 : 1))
              .map((v, i) => (
                <Row key={i} spacing={1}>
                  <Checkbox
                    className="fit"
                    onChange={() => onChangeMultipleData(v.value)}
                    checked={multipleData.includes(v.value)}
                  />
                  {v.code && (
                    <FlagImg country_code={v.code} className="flag-img" />
                  )}
                  <SmText color={COLORS.gray[700]} className="ellipsis-text">
                    {v.label}
                  </SmText>
                </Row>
              ))}
          </Fragment>
        );

      case "SELECT":
        return () => (
          <Fragment>
            {formattedValue.map((v, i) => (
              <Row spacing={1} key={i}>
                <Checkbox
                  className="fit"
                  onChange={() => onChangeSelect(v.value)}
                  checked={getSearchParam(param) === String(v.value)}
                />
                <SmText color={COLORS.gray[700]} className="ellipsis-text">
                  {v.label}
                </SmText>
              </Row>
            ))}
          </Fragment>
        );

      default:
        return () => null;
    }
  }, [filter, multipleData, formattedValue]);

  useEffect(() => {
    setFormattedValue(
      value.filter((value) =>
        value.label.toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [search]);

  return (
    <Fragment>
      {hasSearchOptions.indexOf(type) > -1 && (
        <Input
          className="search-input"
          placeholder={t.formatMessage({ id: "search" })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <Stack spacing={1} className="filter-box">
        <Component />
      </Stack>
    </Fragment>
  );
};

export default ListingFilters;
